import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { useT } from "../i18n/LanguageContext";

const STORAGE_KEY = "hecnos_cookie_consent"; // "accepted" | "declined"

const POSTHOG_KEY = "phc_xAvL2Iq4tFmANRE7kzbKwaSqp1HJjN7x48s3vr0CMjs";
const POSTHOG_HOST = "https://us.i.posthog.com";

const activateTrackers = () => {
  if (typeof window === "undefined" || !window.posthog) return;
  try {
    window.posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      person_profiles: "identified_only",
      session_recording: {
        recordCrossOriginIframes: true,
        capturePerformance: false,
      },
    });
    if (window.posthog.opt_in_capturing) window.posthog.opt_in_capturing();
  } catch (e) {
    // ignore
  }
};

const deactivateTrackers = () => {
  if (typeof window === "undefined" || !window.posthog) return;
  try {
    if (window.posthog.opt_out_capturing) window.posthog.opt_out_capturing();
  } catch (e) {
    // ignore
  }
};

export const CookieConsent = () => {
  const { t } = useT();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      setVisible(true);
    } else if (stored === "accepted") {
      activateTrackers();
    } else {
      deactivateTrackers();
    }
  }, []);

  const accept = () => {
    localStorage.setItem(STORAGE_KEY, "accepted");
    activateTrackers();
    setVisible(false);
  };

  const decline = () => {
    localStorage.setItem(STORAGE_KEY, "declined");
    deactivateTrackers();
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-labelledby="cookie-title"
      aria-describedby="cookie-body"
      className="fixed z-[100] bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-md bg-white border border-black/10 rounded-3xl shadow-[0_20px_60px_-20px_rgba(15,23,42,0.35)] p-6 hx-fadeup"
      data-testid="cookie-consent-banner"
    >
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-2xl bg-[#1093f0]/10 flex items-center justify-center shrink-0">
          <Cookie className="w-5 h-5 text-[#1093f0]" />
        </div>
        <div className="flex-1">
          <h3 id="cookie-title" className="text-lg font-semibold text-[#131517] mb-2">
            {t("cookie.title")}
          </h3>
          <p id="cookie-body" className="text-sm text-[#3f444b] leading-relaxed">
            {t("cookie.body")}{" "}
            <Link
              to="/privacy"
              className="text-[#1093f0] hover:underline"
              data-testid="cookie-consent-privacy-link"
            >
              {t("cookie.linkPrivacy")}
            </Link>
          </p>
        </div>
        <button
          onClick={decline}
          className="sm:hidden text-[#6b7079] hover:text-[#131517] p-1 -mt-1 -mr-1"
          aria-label={t("cookie.decline")}
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      <div className="mt-6 flex flex-col sm:flex-row gap-2 sm:justify-end">
        <button
          onClick={decline}
          data-testid="cookie-consent-decline"
          className="text-sm font-medium px-5 py-2.5 rounded-full border border-black/15 text-[#131517] hover:border-[#131517] transition-colors"
        >
          {t("cookie.decline")}
        </button>
        <button
          onClick={accept}
          data-testid="cookie-consent-accept"
          className="hx-btn-primary inline-flex items-center justify-center text-sm font-semibold px-5 py-2.5 rounded-full bg-[#1093f0] text-white"
        >
          {t("cookie.accept")}
        </button>
      </div>
    </div>
  );
};

export default CookieConsent;
